// src/components/metrics/SampleOrderCostTimeFrame.js

import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  TrendingUp,
  DollarSign,
  Calendar,
  BarChart3,
  Sparkles,
  Download,
} from "lucide-react";
import jsPDF from "jspdf";

const toDateFromAny = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const d = new Date(value.includes("T") ? value : `${value}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const SampleOrderCostTimeFrame = ({ companyId, onCreateSampleOrder, sampleOrders = null }) => {
  const db = getFirestore();
  const [totals, setTotals] = useState({ week: 0, month: 0, year: 0 });
  const [itemsByFrame, setItemsByFrame] = useState({
    week: [],
    month: [],
    year: [],
  });
  const [companyName, setCompanyName] = useState("");
  const [expandedFrame, setExpandedFrame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const externalSampleOrders = useMemo(
    () => (Array.isArray(sampleOrders) ? sampleOrders : null),
    [sampleOrders]
  );

  // Load company name once for the PDF header
  useEffect(() => {
    const loadCompanyName = async () => {
      if (!companyId) return;
      try {
        const companyRef = doc(db, "companies", companyId);
        const snap = await getDoc(companyRef);
        if (snap.exists()) {
          const data = snap.data();
          setCompanyName(data.name || data.companyName || "");
        }
      } catch (err) {
        console.error("[METRIC] Error loading company name:", err);
      }
    };
    loadCompanyName();
  }, [companyId, db]);

  useEffect(() => {
    if (!companyId) return;

    let unsubscribesampleOrders;

    const loadData = async () => {
      setLoading(true);
      setError("");

      try {
        // 1) Load inventory to build productId -> price map
        const inventoryRef = collection(db, "companies", companyId, "inventory");
        const inventorySnap = await getDocs(inventoryRef);

        const priceMap = {};
        inventorySnap.forEach((docSnap) => {
          const data = docSnap.data();
          const price =
            typeof data.price === "number"
              ? data.price
              : Number(data.price) || 0;
          priceMap[docSnap.id] = price;
        });

        console.log(
          "[METRIC] Loaded inventory priceMap keys:",
          Object.keys(priceMap)
        );

        const calculateTotals = (records) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Year bounds
            const startOfYear = new Date(today.getFullYear(), 0, 1);
            startOfYear.setHours(0, 0, 0, 0);
            const endOfYear = new Date(today.getFullYear() + 1, 0, 1);
            endOfYear.setHours(0, 0, 0, 0);

            // Month bounds
            const startOfMonth = new Date(
              today.getFullYear(),
              today.getMonth(),
              1
            );
            startOfMonth.setHours(0, 0, 0, 0);
            const endOfMonth = new Date(
              today.getFullYear(),
              today.getMonth() + 1,
              1
            );
            endOfMonth.setHours(0, 0, 0, 0);

            // Week bounds (Mon–Sun)
            const startOfWeek = new Date(today);
            const jsDay = startOfWeek.getDay(); // 0 = Sun, 1 = Mon, ...
            const diffToMonday = (jsDay + 6) % 7;
            startOfWeek.setDate(startOfWeek.getDate() - diffToMonday);
            startOfWeek.setHours(0, 0, 0, 0);

            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(endOfWeek.getDate() + 7);
            endOfWeek.setHours(0, 0, 0, 0);

            let weekTotal = 0;
            let monthTotal = 0;
            let yearTotal = 0;

            const weekItems = [];
            const monthItems = [];
            const yearItems = [];

            const normalizedRecords = Array.isArray(records) ? records : [];

            console.log(
              "[METRIC] sampleOrders source size:",
              normalizedRecords.length
            );

            normalizedRecords.forEach((record) => {
              const data = record || {};
              const id = record?.id || "";

              // Skip hidden rows
              if (data.hidden === true) {
                return;
              }

              // Skip obviously non-cost statuses
              if (["Cancelled", "Rejected"].includes(data.status)) {
                return;
              }

              // --- Determine effective date ----------------------------------
              let dateSource = null;

              // deliveryDate (string or Timestamp)
              if (data.deliveryDate) {
                if (
                  typeof data.deliveryDate === "object" &&
                  typeof data.deliveryDate.toDate === "function"
                ) {
                  dateSource = data.deliveryDate.toDate();
                } else if (typeof data.deliveryDate === "string") {
                  const d = new Date(data.deliveryDate + "T00:00:00");
                  if (!Number.isNaN(d.getTime())) {
                    dateSource = d;
                  }
                }
              }

              // requestedDate fallback
              if (!dateSource && data.requestedDate) {
                if (
                  typeof data.requestedDate === "object" &&
                  typeof data.requestedDate.toDate === "function"
                ) {
                  dateSource = data.requestedDate.toDate();
                } else if (typeof data.requestedDate === "string") {
                  const d = new Date(data.requestedDate + "T00:00:00");
                  if (!Number.isNaN(d.getTime())) {
                    dateSource = d;
                  }
                }
              }

              // createdAt fallback
              if (!dateSource) {
                dateSource = toDateFromAny(data.createdAt);
              }

              if (!dateSource) {
                return;
              }

              dateSource.setHours(0, 0, 0, 0);

              const productId = data.productId;
              const quantity = Number(data.quantity) || 0;

              if (!productId || quantity <= 0) {
                return;
              }

              const price = priceMap[productId] ?? 0;
              const cost = price * quantity;

              // Build a lightweight item for the detail list
              const itemBase = {
                id,
                date: new Date(dateSource),
                dateStr: dateSource.toISOString().slice(0, 10),
                productName: data.productName || "",
                accountName: data.accountName || "",
                quantity,
                price,
                cost,
                status: data.status || "",
              };

              // Year
              if (dateSource >= startOfYear && dateSource < endOfYear) {
                yearTotal += cost;
                yearItems.push(itemBase);
              }
              // Month
              if (dateSource >= startOfMonth && dateSource < endOfMonth) {
                monthTotal += cost;
                monthItems.push(itemBase);
              }
              // Week
              if (dateSource >= startOfWeek && dateSource < endOfWeek) {
                weekTotal += cost;
                weekItems.push(itemBase);
              }
            });

            console.log(
              "[METRIC] Totals -> week:",
              weekTotal,
              "month:",
              monthTotal,
              "year:",
              yearTotal
            );

            setTotals({
              week: weekTotal,
              month: monthTotal,
              year: yearTotal,
            });
            setItemsByFrame({
              week: weekItems,
              month: monthItems,
              year: yearItems,
            });
            setLoading(false);
        };

        if (externalSampleOrders) {
          calculateTotals(externalSampleOrders);
          return;
        }

        // 2) Subscribe to all sampleOrders for this company
        const sampleRef = collection(
          db,
          "companies",
          companyId,
          "sampleOrders"
        );
        const qSamples = query(sampleRef);

        unsubscribesampleOrders = onSnapshot(
          qSamples,
          (snapshot) => {
            const records = snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }));
            calculateTotals(records);
          },
          (err) => {
            console.error("Error loading sampleOrders for metrics:", err);
            setError(err.message || "Failed to load sample expense data.");
            setLoading(false);
          }
        );
      } catch (err) {
        console.error("Error loading metrics data:", err);
        setError(err.message || "Failed to load sample expense data.");
        setLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubscribesampleOrders) {
        unsubscribesampleOrders();
      }
    };
  }, [companyId, db, externalSampleOrders]);

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    try {
      return num.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return `$${num.toFixed(2)}`;
    }
  };

  const formatShortDate = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      if (Number.isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // We’re not computing changes yet, so hide the pills.
  const weekChange = null;
  const monthChange = null;
  const yearChange = null;

  const toggleFrame = (frame) => {
    setExpandedFrame((prev) => (prev === frame ? null : frame));
  };

  const handleExport = (frameKey) => {
    const items = itemsByFrame[frameKey] || [];
    if (!items.length) {
      alert("No sample orders in this timeframe to export.");
      return;
    }

    const frameLabels = {
      week: "This Week",
      month: "This Month",
      year: "This Year",
    };

    const label = frameLabels[frameKey] || frameKey;

    const doc = new jsPDF();
    const title = companyName || "Sample Expense Report";

    doc.setFontSize(16);
    doc.text(title, 14, 18);

    doc.setFontSize(12);
    doc.text(`Sample Expense – ${label}`, 14, 26);

    // Table headers
    let y = 36;
    doc.setFontSize(9);
    doc.text("Date", 14, y);
    doc.text("Product", 34, y);
    doc.text("Account", 94, y);
    doc.text("Qty", 144, y);
    doc.text("Price", 160, y);
    doc.text("Cost", 180, y, { align: "right" });
    y += 5;

    const pageHeight = doc.internal.pageSize.getHeight();

    items.forEach((item) => {
      if (y > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }

      const dateLabel = formatShortDate(item.dateStr);
      const productLabel = (item.productName || "").slice(0, 30);
      const accountLabel = (item.accountName || "").slice(0, 24);

      doc.text(dateLabel, 14, y);
      doc.text(productLabel, 34, y);
      doc.text(accountLabel, 94, y);
      doc.text(String(item.quantity), 144, y);
      doc.text(formatCurrency(item.price), 160, y);
      doc.text(formatCurrency(item.cost), 190, y, { align: "right" });
      y += 4;
    });

    // Totals row
    const totalValue = totals[frameKey] || 0;
    y += 4;
    doc.setFontSize(10);
    doc.text("Total:", 160, y);
    doc.text(formatCurrency(totalValue), 190, y, { align: "right" });

    const filename = `sample-expense-${frameKey}-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    doc.save(filename);
  };

  const TimeframeCard = ({
    frameKey,
    label,
    amount,
    change,
    icon: Icon,
    gradient,
    delay,
    isActive,
    onClick,
    onExport,
  }) => (
    <div
      className="relative group"
      style={{
        animation: `slideUp 0.5s ease-out ${delay}s both`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-300 blur-xl"></div>

      <div
        className={`relative bg-white border rounded-xl px-3 py-3 flex flex-col gap-2 hover:shadow-md transition-all duration-200 cursor-pointer ${
          isActive
            ? "border-blue-500 ring-1 ring-blue-100"
            : "border-gray-200 hover:border-gray-300"
        }`}
        onClick={onClick}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg bg-gradient-to-br ${gradient}`}>
              <Icon className="w-4 h-4 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                {label}
              </span>
              <span className="text-lg font-semibold text-gray-900 tracking-tight">
                {formatCurrency(amount)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {change !== null && (
              <div
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  change >= 0
                    ? "bg-green-50 text-green-700"
                    : "bg-red-50 text-red-700"
                }`}
              >
                <TrendingUp
                  className={`w-3 h-3 ${change < 0 ? "rotate-180" : ""}`}
                />
                {Math.abs(change).toFixed(1)}%
              </div>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onExport();
              }}
              className="p-1 rounded-md hover:bg-gray-100 text-gray-500"
              title={`Export ${label} to PDF`}
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Subtle animated underline */}
        <div
          className={`h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent transition-opacity duration-300 ${
            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        ></div>
      </div>
    </div>
  );

  const renderExpandedList = () => {
    if (!expandedFrame) return null;

    const frameLabels = {
      week: "This Week",
      month: "This Month",
      year: "This Year",
    };

    const items = itemsByFrame[expandedFrame] || [];
    const label = frameLabels[expandedFrame] || expandedFrame;

    return (
      <div className="mt-4 border border-gray-200 rounded-xl bg-white">
        <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700">
            {label} – {items.length} sample
            {items.length === 1 ? "" : "s"}
          </span>
          <span className="text-[11px] text-gray-400">
            Detail view (date, product, account, qty, price, cost)
          </span>
        </div>

        {items.length === 0 ? (
          <div className="px-3 py-2 text-[11px] text-gray-500">
            No sample orders in this timeframe yet.
          </div>
        ) : (
          <div className="max-h-44 overflow-auto">
            <div className="text-[11px] text-gray-500 border-b border-gray-100 px-3 py-1 grid grid-cols-[80px_minmax(0,1.4fr)_minmax(0,1.1fr)_40px_60px_70px] gap-2">
              <span>Date</span>
              <span>Product</span>
              <span>Account</span>
              <span className="text-right">Qty</span>
              <span className="text-right">Price</span>
              <span className="text-right">Cost</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="px-3 py-1.5 text-[11px] text-gray-700 grid grid-cols-[80px_minmax(0,1.4fr)_minmax(0,1.1fr)_40px_60px_70px] gap-2 items-center"
                >
                  <span className="text-gray-500">
                    {formatShortDate(item.dateStr)}
                  </span>
                  <span className="truncate" title={item.productName}>
                    {item.productName || "-"}
                  </span>
                  <span className="truncate" title={item.accountName}>
                    {item.accountName || "-"}
                  </span>
                  <span className="text-right">
                    {item.quantity ?? "-"}
                  </span>
                  <span className="text-right">
                    {formatCurrency(item.price)}
                  </span>
                  <span className="text-right font-medium">
                    {formatCurrency(item.cost)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50/30 border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header Section */}
      <div className="relative px-4 py-4 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="absolute inset-0 bg-black/5"></div>
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

        <div className="relative flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                Sample Expense Tracker
                <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
              </h3>
              <p className="text-[11px] text-blue-100">
                Real-time projected sample costs
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {loading && (
              <div className="flex items-center gap-1 text-white/80 text-[11px]">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                <span>Syncing…</span>
              </div>
            )}

            {onCreateSampleOrder && (
              <button
                type="button"
                onClick={onCreateSampleOrder}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/90 text-blue-700 text-xs font-medium shadow-sm hover:bg-white"
              >
                <span className="text-blue-600 text-base leading-none">
                  +
                </span>
                <span>Create Sample Order</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4">
        {error && (
          <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start gap-2">
            <div className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-red-600">!</span>
            </div>
            <span>{error}</span>
          </div>
        )}

        {!error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TimeframeCard
                frameKey="week"
                label="This Week"
                amount={totals.week}
                change={weekChange}
                icon={Calendar}
                gradient="from-blue-500 to-blue-600"
                delay={0}
                isActive={expandedFrame === "week"}
                onClick={() => toggleFrame("week")}
                onExport={() => handleExport("week")}
              />

              <TimeframeCard
                frameKey="month"
                label="This Month"
                amount={totals.month}
                change={monthChange}
                icon={BarChart3}
                gradient="from-purple-500 to-purple-600"
                delay={0.05}
                isActive={expandedFrame === "month"}
                onClick={() => toggleFrame("month")}
                onExport={() => handleExport("month")}
              />

              <TimeframeCard
                frameKey="year"
                label="This Year"
                amount={totals.year}
                change={yearChange}
                icon={TrendingUp}
                gradient="from-indigo-500 to-indigo-600"
                delay={0.1}
                isActive={expandedFrame === "year"}
                onClick={() => toggleFrame("year")}
                onExport={() => handleExport("year")}
              />
            </div>

            {/* Slim expanded list below tiles */}
            {renderExpandedList()}

            {/* Footer Insight */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px]">
              <span className="text-gray-500">
                Uses Delivery Date where set, otherwise requested date
              </span>
              <span className="text-gray-400 font-medium">
                Last updated:{" "}
                {new Date().toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })}
              </span>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default SampleOrderCostTimeFrame;
